import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Chip,
  Typography,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  TableSortLabel,
  Toolbar,
} from "@mui/material";
import { Icon } from "@iconify/react";
import {
  useGetEventQuery,
  useAddAttendeesMutation,
  useRemoveAttendeesMutation,
} from "../../features/events/eventsApiSlice";
import { useGetMembersQuery } from "../../features/members/membersApiSlice";
import { useDispatch } from "react-redux";
import { setDialog } from "../../features/dialog/dialogSlice";
import { gradArray } from "../form/fields/inputFields";

const toId = (x) => x?._id || x?.id || String(x);
const gradLabel = (degree) =>
  gradArray.find((g) => g.value === Number(degree))?.label ||
  String(degree || "");

const labelOf = (m) =>
  `${m?.lastName ?? ""}${m?.lastName ? ", " : ""}${m?.firstName ?? ""}`.trim();

export default function AttendeesDialog({
  open,
  onClose,
  eventId,
  eventTitle,
}) {
  const dispatch = useDispatch();

  // Event with (possibly populated) attendees
  const {
    data: event,
    isFetching: isFetchingEvent,
    refetch,
  } = useGetEventQuery(eventId, {
    skip: !eventId,
  });
  const [addAttendees, { isLoading: isAdding }] = useAddAttendeesMutation();
  const [removeAttendees, { isLoading: isRemoving }] =
    useRemoveAttendeesMutation();

  // All members
  const { data: members = [], isLoading: isLoadingMembers } =
    useGetMembersQuery();

  // Current attendee ids
  const attendeeIds = useMemo(() => {
    if (!event?.hasAttendees) return new Set();
    return new Set(event.hasAttendees.map((a) => toId(a)));
  }, [event]);

  // Build sites list from members (standortRef)
  const allSites = useMemo(() => {
    const map = new Map();
    (members || []).forEach((m) => {
      const sr = m?.standortRef;
      const id = typeof sr === "object" ? sr?._id : sr;
      const name = typeof sr === "object" ? sr?.siteName : undefined;
      if (id) map.set(id, name || id);
    });
    return Array.from(map.entries()).map(([id, siteName]) => ({
      id,
      siteName,
    }));
  }, [members]);

  // Left table: only ACTIVE members not yet in event
  const activeNotAttending = useMemo(
    () =>
      (members || [])
        .filter((m) => m?.status === "active")
        .filter((m) => !attendeeIds.has(toId(m))),
    [members, attendeeIds],
  );

  // Filters & sorting
  const [siteFilter, setSiteFilter] = useState("ALL");
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState("lastName"); // 'lastName' | 'firstName' | 'degree'
  const [sortOrder, setSortOrder] = useState("asc"); // 'asc' | 'desc'

  // Selection
  const [selectedIds, setSelectedIds] = useState([]);

  useEffect(() => {
    if (!open) {
      setSelectedIds([]);
      setSiteFilter("ALL");
      setSearch("");
      setSortKey("lastName");
      setSortOrder("asc");
    }
  }, [open]);

  const filtered = useMemo(() => {
    let arr = activeNotAttending;

    // filter by site
    if (siteFilter !== "ALL") {
      arr = arr.filter((m) => {
        const sr = m?.standortRef;
        const id = typeof sr === "object" ? sr?._id : sr;
        return id === siteFilter;
      });
    }

    // simple search on name and memberNumber
    if (search.trim()) {
      const q = search.toLowerCase();
      arr = arr.filter((m) =>
        [
          m?.firstName ?? "",
          m?.lastName ?? "",
          m?.memberNumber ?? "",
          (typeof m?.standortRef === "object" ? m.standortRef?.siteName : "") ??
            "",
        ]
          .join(" ")
          .toLowerCase()
          .includes(q),
      );
    }

    // sort
    const order = sortOrder === "asc" ? 1 : -1;
    arr = [...arr].sort((a, b) => {
      const av = a?.[sortKey] ?? "";
      const bv = b?.[sortKey] ?? "";
      if (sortKey === "degree") {
        const an = Number(av) || 0;
        const bn = Number(bv) || 0;
        return (an - bn) * order;
      }
      return (
        String(av).localeCompare(String(bv), undefined, { numeric: true }) *
        order
      );
    });

    return arr;
  }, [activeNotAttending, siteFilter, search, sortKey, sortOrder]);

  const toggleAll = (checked) => {
    setSelectedIds(checked ? filtered.map((m) => toId(m)) : []);
  };

  const isSelected = (id) => selectedIds.includes(id);

  const toggleOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const selectedMembers = useMemo(() => {
    const set = new Set(selectedIds);
    return filtered.filter((m) => set.has(toId(m)));
  }, [filtered, selectedIds]);

  const currentAttendeesForRight = useMemo(() => {
    const list = Array.isArray(event?.hasAttendees) ? event.hasAttendees : [];
    // If populated, we can show names; otherwise show id chips
    return list.map((a) => {
      const id = toId(a);
      const firstName = a?.firstName;
      const lastName = a?.lastName;
      const label =
        firstName || lastName
          ? `${lastName ?? ""}${lastName ? ", " : ""}${firstName ?? ""}`.trim()
          : id;
      const degree = a?.degree;
      return { id, label, degree };
    });
  }, [event]);

  const handleAddSelected = async () => {
    if (selectedIds.length === 0) return;
    try {
      await addAttendees({ id: eventId, memberIds: selectedIds }).unwrap();
      setSelectedIds([]);
      await refetch();
      dispatch(
        setDialog({
          type: "SUCCESS",
          title: "Teilnehmer hinzugefügt",
          data: {
            successText: `${selectedIds.length} Teilnehmer hinzugefügt.`,
          },
        }),
      );
    } catch (e) {
      dispatch(
        setDialog({
          type: "ERROR",
          title: "Fehler",
          data: { errorText: e?.data?.message || "Hinzufügen fehlgeschlagen." },
        }),
      );
    }
  };

  const handleRemoveOne = async (id) => {
    try {
      await removeAttendees({ id: eventId, memberIds: [id] }).unwrap();
      await refetch();
      dispatch(
        setDialog({
          type: "SUCCESS",
          title: "Teilnehmer entfernt",
          data: { successText: "Teilnehmer wurde entfernt." },
        }),
      );
    } catch (e) {
      dispatch(
        setDialog({
          type: "ERROR",
          title: "Fehler",
          data: { errorText: e?.data?.message || "Entfernen fehlgeschlagen." },
        }),
      );
    }
  };

  const busy = isFetchingEvent || isLoadingMembers || isAdding || isRemoving;

  // ----- UI -----
  return (
    <Dialog
      open={open}
      onClose={() => onClose?.(false)}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>Teilnehmer · {eventTitle}</DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            {/* LEFT: selection table */}
            <Paper variant="outlined" sx={{ flex: 1, overflow: "hidden" }}>
              <Toolbar sx={{ gap: 2, mt: 2, flexWrap: "wrap" }}>
                <FormControl size="small" sx={{ minWidth: 220 }}>
                  <InputLabel id="site-filter-label">Standort</InputLabel>
                  <Select
                    labelId="site-filter-label"
                    label="Standort"
                    value={siteFilter}
                    onChange={(e) => setSiteFilter(e.target.value)}
                  >
                    <MenuItem value="ALL">Alle Standorte</MenuItem>
                    {allSites.map((s) => (
                      <MenuItem key={s.id} value={s.id}>
                        {s.siteName || s.id}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  size="small"
                  placeholder="Suche (Name, Nr., Standort)"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  sx={{ minWidth: 240 }}
                />

                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel id="sort-key-label">Sortieren nach</InputLabel>
                  <Select
                    labelId="sort-key-label"
                    label="Sortieren nach"
                    value={sortKey}
                    onChange={(e) => setSortKey(e.target.value)}
                  >
                    <MenuItem value="lastName">Nachname</MenuItem>
                    <MenuItem value="firstName">Vorname</MenuItem>
                    <MenuItem value="degree">Grad</MenuItem>
                  </Select>
                </FormControl>

                <Button
                  size="small"
                  variant="outlined"
                  onClick={() =>
                    setSortOrder((p) => (p === "asc" ? "desc" : "asc"))
                  }
                  startIcon={
                    <Icon
                      icon={
                        sortOrder === "asc"
                          ? "mdi:sort-ascending"
                          : "mdi:sort-descending"
                      }
                    />
                  }
                >
                  {sortOrder === "asc" ? "Aufsteigend" : "Absteigend"}
                </Button>
              </Toolbar>

              <Divider />

              <TableContainer sx={{ maxHeight: 420 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          indeterminate={
                            selectedIds.length > 0 &&
                            selectedIds.length < filtered.length
                          }
                          checked={
                            filtered.length > 0 &&
                            selectedIds.length === filtered.length
                          }
                          onChange={(e) => toggleAll(e.target.checked)}
                        />
                      </TableCell>
                      <TableCell
                        sortDirection={
                          sortKey === "lastName" ? sortOrder : false
                        }
                      >
                        <TableSortLabel
                          active={sortKey === "lastName"}
                          direction={sortKey === "lastName" ? sortOrder : "asc"}
                          onClick={() => {
                            if (sortKey === "lastName")
                              setSortOrder((p) =>
                                p === "asc" ? "desc" : "asc",
                              );
                            setSortKey("lastName");
                          }}
                        >
                          Nachname
                        </TableSortLabel>
                      </TableCell>
                      <TableCell
                        sortDirection={
                          sortKey === "firstName" ? sortOrder : false
                        }
                      >
                        <TableSortLabel
                          active={sortKey === "firstName"}
                          direction={
                            sortKey === "firstName" ? sortOrder : "asc"
                          }
                          onClick={() => {
                            if (sortKey === "firstName")
                              setSortOrder((p) =>
                                p === "asc" ? "desc" : "asc",
                              );
                            setSortKey("firstName");
                          }}
                        >
                          Vorname
                        </TableSortLabel>
                      </TableCell>
                      <TableCell
                        sortDirection={sortKey === "degree" ? sortOrder : false}
                      >
                        <TableSortLabel
                          active={sortKey === "degree"}
                          direction={sortKey === "degree" ? sortOrder : "asc"}
                          onClick={() => {
                            if (sortKey === "degree")
                              setSortOrder((p) =>
                                p === "asc" ? "desc" : "asc",
                              );
                            setSortKey("degree");
                          }}
                        >
                          Grad
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>Mitgliedsnummer</TableCell>
                      <TableCell>Standort</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {filtered.map((m) => {
                      const id = toId(m);
                      const checked = isSelected(id);
                      const siteName =
                        typeof m?.standortRef === "object"
                          ? m?.standortRef?.siteName
                          : allSites.find((s) => s.id === m?.standortRef)
                              ?.siteName || "";

                      return (
                        <TableRow
                          key={id}
                          hover
                          selected={checked}
                          onClick={() => toggleOne(id)}
                          sx={{ cursor: "pointer" }}
                        >
                          <TableCell
                            padding="checkbox"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Checkbox
                              checked={checked}
                              onChange={() => toggleOne(id)}
                            />
                          </TableCell>
                          <TableCell>{m?.lastName}</TableCell>
                          <TableCell>{m?.firstName}</TableCell>
                          <TableCell>{gradLabel(m?.degree)}</TableCell>
                          <TableCell>{m?.memberNumber ?? ""}</TableCell>
                          <TableCell>{siteName}</TableCell>
                        </TableRow>
                      );
                    })}

                    {!busy && filtered.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6}>
                          <Typography
                            variant="body2"
                            sx={{ py: 2, color: "text.secondary" }}
                          >
                            Keine Mitglieder gefunden (Filter prüfen).
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            {/* RIGHT: current + selected */}
            <Stack sx={{ width: { xs: "100%", md: 380 } }} spacing={2}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Bereits angemeldet ({currentAttendeesForRight.length})
                </Typography>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  {currentAttendeesForRight.map((a) => (
                    <Chip
                      key={a.id}
                      label={
                        a.degree
                          ? `${a.label} · ${gradLabel(a.degree)}`
                          : a.label
                      }
                      size="small"
                      onDelete={() => handleRemoveOne(a.id)}
                      deleteIcon={<Icon icon="mdi:close" />}
                    />
                  ))}
                  {currentAttendeesForRight.length === 0 && (
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary" }}
                    >
                      Noch keine Teilnehmer.
                    </Typography>
                  )}
                </Stack>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Neu ausgewählt ({selectedMembers.length})
                </Typography>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  {selectedMembers.map((m) => (
                    <Chip
                      key={toId(m)}
                      label={`${labelOf(m)} · ${gradLabel(m?.degree)}`}
                      size="small"
                      onDelete={() => toggleOne(toId(m))}
                    />
                  ))}
                  {selectedMembers.length === 0 && (
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary" }}
                    >
                      Noch keine Auswahl.
                    </Typography>
                  )}
                </Stack>
              </Paper>

              <Button
                variant="contained"
                color="secondary"
                startIcon={<Icon icon="mdi:account-multiple-plus" />}
                disabled={busy || selectedIds.length === 0}
                onClick={handleAddSelected}
              >
                {selectedIds.length} Teilnehmer hinzufügen
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={() => onClose?.(false)}>Schließen</Button>
        <Button
          onClick={() => onClose?.(true)}
          variant="contained"
          disabled={busy}
        >
          Fertig
        </Button>
      </DialogActions>
    </Dialog>
  );
}

AttendeesDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  eventId: PropTypes.string.isRequired,
  eventTitle: PropTypes.string,
};
